import { NativeModules, Platform } from "react-native";
import { get as storageGet, set as storageSet, remove as storageRemove } from "@lib/storage";
import type { ResolvedValidationConfig } from "@utils/proofValidationConfig";

/**
 * Thin wrapper over the native delivery-proof capture screen
 * (live.citymall.partnerdb.proof.ProofCaptureModule).
 *
 * Everything here exists so a failure of the native path degrades to the old
 * react-native-image-picker flow instead of stranding a delivery. Callers are
 * expected to check `isNativeCaptureAvailable()` and fall back when it is false,
 * and to treat a rejected `captureProof()` the same way.
 */

export interface ProofValidation {
  verdict: "PASS" | "FORCED" | "SKIPPED";
  mode: string;
  attempts: number;
  ml_skipped: boolean;
  objects: number;
  scores: Record<string, number>;
  failed: string[];
  skipped: string[];
  /** goods_present only — what the classifier saw, e.g. "Car"; absent otherwise. */
  subject?: string | null;
  /** "person" | "vehicle" | "building" | "other" from the server's deny_labels keys. */
  subject_category?: string | null;
  /** Top classifier labels across detected objects (max 10), whenever detection ran. */
  labels?: { label: string; confidence: number }[];
}

export interface NativeCaptureResponse {
  didCancel: boolean;
  uri?: string | null;
  fileName?: string | null;
  type?: string | null;
  fileSize?: number;
  width?: number;
  height?: number;
  validation?: ProofValidation | null;
}

interface ProofCaptureNativeModule {
  capture(config: ResolvedValidationConfig): Promise<NativeCaptureResponse>;
}

const nativeModule: ProofCaptureNativeModule | undefined =
  NativeModules?.ProofCaptureModule;

/**
 * Device-local kill switch — the third bypass lever, and the only one that
 * needs no network.
 *
 * Both server levers reach the device over HTTP. A phone whose camera stack is
 * genuinely broken can reach neither in time to matter, so after a few
 * consecutive init failures we stop trying and let the old picker take over
 * until the next successful boot clears the counter.
 */
const FAILURE_KEY = "proof_capture_consecutive_failures";
const DISABLE_AFTER_FAILURES = 3;

const readFailureCount = (): number => {
  try {
    return Number(storageGet(FAILURE_KEY)) || 0;
  } catch {
    return 0;
  }
};

export const recordCaptureFailure = (): void => {
  try {
    storageSet(FAILURE_KEY, String(readFailureCount() + 1));
  } catch {
    // Storage is not worth failing a delivery over.
  }
};

export const resetCaptureFailures = (): void => {
  try {
    storageRemove(FAILURE_KEY);
  } catch {
    // As above.
  }
};

/**
 * MANDATORY guard, not defensive polish. Revopush ships JS bundles without a
 * native binary, so a CodePush update that assumes this module exists would
 * hard-fail on every device still running an older APK.
 */
export const isNativeCaptureAvailable = (): boolean =>
  Platform.OS === "android" &&
  !!nativeModule?.capture &&
  readFailureCount() < DISABLE_AFTER_FAILURES;

export const captureProof = async (
  config: ResolvedValidationConfig,
): Promise<NativeCaptureResponse> => {
  if (!nativeModule?.capture) {
    throw new Error("ProofCaptureModule unavailable");
  }
  try {
    const response = await nativeModule.capture(config);
    resetCaptureFailures();
    return response;
  } catch (e) {
    // A cancel resolves rather than rejects, so anything landing here is a real
    // failure: no foreground activity, camera unavailable, module invalidated.
    recordCaptureFailure();
    throw e;
  }
};
